import React, { useContext } from 'react';
import classNames from 'classnames';
import { Intl } from '../../../../../constants';
import { Button, Drawer, LeftOutlined, RightOutlined, SvgIcon } from '@fx-ui/fine-design';
import { BaseArgumentValue, PluginArgumentField } from '../../../../../interfaces';
import { cloneDeep, map, size } from '@libs/shared';
import PluginComponent, { PluginComponentContext } from '../../../plugin-component';
import ContractIcon from '../../../../../assets/svgicons/contract.svg';

interface RowEditorProps {
    visible: boolean;
    title: string;
    fields?: PluginArgumentField[];
    values?: BaseArgumentValue[][];
    rowIndex?: number;
    onClose: () => void;
    onAdd: () => void;
    onRowChange: (rowIndex: number) => void;
}

const RowEditor: React.FC<RowEditorProps> = (props) => {
    const { title, visible, rowIndex, fields, values, onAdd, onRowChange, onClose } = props;

    const pluginComponentStore = useContext(PluginComponentContext);

    if (!pluginComponentStore) {
        return null;
    }

    const totalSize = size(values);

    const renderContent = () => {
        if (!visible) {
            return null;
        }

        const { onChange } = pluginComponentStore;

        return (
            <div className="body-wrapper">
                <div className="row-content">
                    {map(fields, (item, colIndex) => {
                        return (
                            <div key={item.id}>
                                <PluginComponent
                                    {...pluginComponentStore}
                                    field={item}
                                    fieldValue={values![rowIndex!]![colIndex]!}
                                    hideResolverTypeSelect
                                    onChange={(v) => {
                                        const temp = cloneDeep(values!);
                                        temp[rowIndex!]![colIndex] = {
                                            ...temp[rowIndex!]![colIndex],
                                            ...v
                                        } as any as BaseArgumentValue;
                                        onChange(temp);
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="footer">
                    <Button
                        type="negative"
                        onClick={() => {
                            onAdd();
                            onRowChange(totalSize);
                        }}
                    >
                        {Intl.get('complete.and.create')}
                    </Button>
                    <Button type="primary" onClick={onClose}>
                        {Intl.get('complete')}
                    </Button>
                </div>
            </div>
        );
    };

    const renderTitle = () => {
        const currentRowNum = rowIndex! + 1;
        const prevBtnDisabled = currentRowNum === 1;
        const nextBtnDisabled = currentRowNum === totalSize;

        return (
            <div className="header-wrapper">
                <div className="header-title">{title}</div>
                <div className="data-switch">
                    <LeftOutlined
                        className={classNames('prev-btn', {
                            disabled: prevBtnDisabled
                        })}
                        onClick={() => {
                            if (prevBtnDisabled) {
                                return;
                            }
                            onRowChange(rowIndex! - 1);
                        }}
                    />
                    <span className="data-text">{`${currentRowNum}/${totalSize}`}</span>
                    <RightOutlined
                        className={classNames('next-btn', {
                            disabled: nextBtnDisabled
                        })}
                        onClick={() => {
                            if (nextBtnDisabled) {
                                return;
                            }
                            onRowChange(rowIndex! + 1);
                        }}
                    />
                </div>
            </div>
        );
    };

    return (
        <Drawer
            className="fx-vector-row-editor"
            visible={visible}
            title={renderTitle()}
            placement="right"
            edge={{ width: 520 }}
            closeIcon={<SvgIcon component={ContractIcon} />}
            onClose={onClose}
        >
            {renderContent()}
        </Drawer>
    );
};

export default RowEditor;
